// Sentry beforeSend filters for errors thrown entirely inside third-party code we can't fix.
// Each matcher requires a stack-frame condition, never just a message, so an error with the
// same message coming from our own code is still reported.

const getFrames = (exception) => exception?.stacktrace?.frames || [];

// Frames the stack parser couldn't attribute to a script (native code, eval, worker bootstrap).
const isUnattributedFrame = (filename) =>
    !filename || filename === '<anonymous>' || filename === '[native code]' || /^(blob|data):/.test(filename);

// INC-1759 / INC-1760: Monaco is loaded from jsDelivr by @monaco-editor/react. When its
// TypeScript language worker (used for JavaScript/TypeScript IntelliSense) crashes internally
// ("Debug Failure. False expression: ...", "Cannot read properties of undefined (reading
// 'length')"), Monaco re-throws the worker's error on the main thread from a setTimeout, so
// every frame points at the CDN's ts.worker script — none of it is our code.
const MONACO_CDN_FRAME = /\/\/cdn\.jsdelivr\.net\/npm\/monaco-editor@[^/]+\/.*\.js/;
const MONACO_TS_WORKER_FRAME = /\/monaco-editor@[^/]+\/.*\/ts\.worker[^/]*\.js/;

const isMonacoTsWorkerError = (exception) => {
    const filenames = getFrames(exception).map((frame) => frame.filename);
    return (
        filenames.some((filename) => MONACO_TS_WORKER_FRAME.test(filename || '')) &&
        filenames.every((filename) => isUnattributedFrame(filename) || MONACO_CDN_FRAME.test(filename))
    );
};

// INC-1761: Excalidraw's text-to-diagram / Mermaid code throws a message-less Error from an
// async callback in the whiteboard. Excalidraw and Mermaid are bundled into our own hashed
// /static/js/ chunks, so their frames can't be told apart from ours by filename on the client.
// Instead this only matches when: the Error has no message at all (Sentry substitutes
// "No error message"; src/ never throws a message-less Error), every frame is one of our own
// same-origin webpack chunks (not an extension or other injected script), and the Excalidraw
// whiteboard is mounted on the page.
const BUNDLE_CHUNK_PATH = /^\/static\/js\/[^/]+\.js$/;

const isOwnBundleFrame = (filename) => {
    try {
        const url = new URL(filename);
        return url.origin === window.location.origin && BUNDLE_CHUNK_PATH.test(url.pathname);
    } catch {
        return false;
    }
};

const isExcalidrawMessagelessError = (exception) => {
    if (exception?.type !== 'Error' || exception?.value !== 'No error message') return false;
    if (exception?.mechanism?.type !== 'instrument') return false;
    if (typeof document === 'undefined' || !document.querySelector('.excalidraw')) return false;

    const filenames = getFrames(exception).map((frame) => frame.filename);
    return (
        filenames.some((filename) => isOwnBundleFrame(filename)) &&
        filenames.every((filename) => isUnattributedFrame(filename) || isOwnBundleFrame(filename))
    );
};

export const isThirdPartyNoise = (event) => {
    const exceptions = event?.exception?.values;
    if (!exceptions?.length) return false;
    // Sentry puts the outermost (thrown) error last.
    const exception = exceptions[exceptions.length - 1];
    return isMonacoTsWorkerError(exception) || isExcalidrawMessagelessError(exception);
};
